/**
 * NavbarUserMenu — desktop avatar dropdown for the top-right of Navbar.
 * Shows a skeleton pulse while auth is loading, an avatar dropdown when
 * signed in, and a "Sign In" button (rendered by the caller) otherwise.
 */
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Store, Package, User, QrCode, Shield, Settings, LogOut } from "lucide-react";

export default function NavbarUserMenu({ user, loading, onLogout }) {
  if (loading) {
    return <div className="w-10 h-10 bg-gray-100 rounded-full animate-pulse" />;
  }
  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2" data-testid="user-menu-trigger">
          <Avatar className="w-9 h-9 border-2 border-gray-100">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-tbc-navy text-white text-sm">
              {user.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <p className="font-medium text-tbc-navy">{user.name}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="cursor-pointer" data-testid="dropdown-dashboard">
            <Store className="w-4 h-4 mr-2" />
            Dashboard
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/orders" className="cursor-pointer" data-testid="dropdown-orders">
            <Package className="w-4 h-4 mr-2" />
            Orders
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer" data-testid="dropdown-profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/cards" className="cursor-pointer" data-testid="dropdown-cards">
            <QrCode className="w-4 h-4 mr-2" />
            My Cards
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/privacy" className="cursor-pointer" data-testid="dropdown-legal">
            <Shield className="w-4 h-4 mr-2" />
            Legal &amp; Privacy
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/admin" className="cursor-pointer" data-testid="dropdown-admin">
            <Settings className="w-4 h-4 mr-2" />
            Admin Panel
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          className="text-red-600 cursor-pointer"
          data-testid="dropdown-logout"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
